import React from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import ptBR from 'dayjs/locale/pt-br';
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import * as yup from 'yup';
import DayjsUtils from '@date-io/dayjs';

import ActionsButtonsBaseModal from 'components/atoms/ActionsButtonsBaseModal';
import { getYesOrNotOption } from 'services/activity';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import { postReplanDailyBulletin } from 'services/daily-bulletin';
import { IModalReplanProps, IOption } from './types';
import useStyles from './style';

const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_OK_TEXT = 'Obrigatório';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido.';

const FORM_ID = 'REPLANEJAR_FORM_MODAL';

// const MIN_DATE = dayjs().format('YYYY-MM-DD');
const MAX_DATE = dayjs('2100-01-01');

const ModalReplan = ({
  children,
  open,
  gridRow,
  onClose,
  reloadData,
}: IModalReplanProps) => {
  const classes = useStyles();
  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);

  const [outageOptions, setOutageOptions] = React.useState<IOption[]>([]);
  const [utilizationOptions, setUtilizationOptions] = React.useState<IOption[]>(
    [],
  );

  const validationSchema = yup.object({
    replanStartDate: yup
      .mixed()
      .required(DEFAULT_OK_TEXT)
      .test('is-greater-than', DEFAULT_ERROR_TEXT, value => {
        // const test = dayjs(value);
        return dayjs(value).isValid();
        // && (test.isSame(MIN_DATE) || test.isAfter(MIN_DATE))
      }),
    replanFinishDate: yup
      .mixed()
      .required(DEFAULT_OK_TEXT)
      // eslint-disable-next-line func-names
      .test('is-greater-than', DEFAULT_ERROR_TEXT, function (value) {
        const test = dayjs(value);
        // eslint-disable-next-line react/no-this-in-sfc
        const { replanStartDate } = this.parent;

        const validation =
          // não gera erro se o início previsto n for digitado
          !replanStartDate ||
          // testa a data e verifica a validação de data mínima,
          // o segundo parâmetro utilizado é a granularidade da comparação
          (test.isValid() &&
            (test.isSame(replanStartDate, 'minute') ||
              test.isAfter(replanStartDate, 'minute')));

        return validation;
      }),
    outage: yup.string().required(DEFAULT_OK_TEXT),
    utilization: yup.string().required(DEFAULT_OK_TEXT),
  });

  const formik = useFormik({
    initialValues: {
      replanStartDate: null,
      replanFinishDate: null,
      outage: '',
      utilization: '',
    },
    validationSchema,
    onSubmit: formValues => {
      const { replanStartDate, replanFinishDate, outage, utilization } =
        formValues;

      const params = {
        id: gridRow?.activityPlanId,
        start: dayjs(replanStartDate).format('YYYY-MM-DDTHH:mmZ'),
        completion: dayjs(replanFinishDate).format('YYYY-MM-DDTHH:mmZ'),
        outage,
        utilization,
      };

      setLoadingSaveForm(true);
      postReplanDailyBulletin(params)
        .then(() => {
          if (reloadData) reloadData();
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const enableQueries = React.useMemo(() => open, [open]);

  const enableOutageOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const enableUtilizationOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const queryOutageOptions = useQuery({
    queryKey: ['outageOptionsContext'],
    queryFn: async () => {
      const { data } = await getYesOrNotOption({});
      return data;
    },
    select: data => {
      return Object.values(data).map(value => {
        return {
          label: value === 'S' ? 'Sim' : 'Não',
          value,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setOutageOptions(data);
    },
    enabled: enableOutageOptions,
  });

  const queryUtilizationOptions = useQuery({
    queryKey: ['utilizationOptionsContext'],
    queryFn: async () => {
      const { data } = await getYesOrNotOption({});
      return data;
    },
    select: data => {
      return Object.values(data).map(value => {
        return {
          label: value === 'S' ? 'Sim' : 'Não',
          value,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setUtilizationOptions(data);
    },
    enabled: enableUtilizationOptions,
  });

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldTouched(name);
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  React.useEffect(() => {
    if (formik.dirty && !open) {
      formik.resetForm();
    }
  }, [formik, open]);

  const renderBody = React.useCallback(() => {
    return (
      <form id={FORM_ID} onSubmit={formik.handleSubmit}>
        <Grid container className={classes.bodyContent} spacing={0}>
          {/* START ROW 0 */}
          <Grid>
            <Typography className={classes.formMessage}>
              Preencha os dados obrigatórios para replanejar a atividade:
            </Typography>
          </Grid>
          {/* END ROW 0 */}

          {/* END ROW 1 */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <KeyboardDateTimePicker
                id="replanStartDate"
                name="replanStartDate"
                label="Data de início"
                inputVariant="outlined"
                format="DD/MM/YY - HH:mm"
                cancelLabel="Cancelar"
                clearLabel="Limpar"
                clearable
                ampm={false}
                // minDate={MIN_DATE}
                maxDate={MAX_DATE}
                onChange={date => handleDateChange('replanStartDate', date)}
                value={formik.values.replanStartDate}
                error={
                  formik.touched.replanStartDate &&
                  Boolean(formik.errors.replanStartDate)
                }
                helperText={
                  (formik.touched.replanStartDate &&
                    formik.errors.replanStartDate) ||
                  DEFAULT_HELPER_TEXT
                }
              />
            </Grid>
            <Grid item xs={6}>
              <KeyboardDateTimePicker
                id="replanFinishDate"
                name="replanFinishDate"
                label="Data de conclusão"
                inputVariant="outlined"
                format="DD/MM/YY - HH:mm"
                cancelLabel="Cancelar"
                clearLabel="Limpar"
                clearable
                ampm={false}
                // validacao temporariamente comentada a pedido da cliente
                // minDate={formik.values?.replanStartDate || MIN_DATE}
                minDate={formik.values?.replanStartDate}
                maxDate={MAX_DATE}
                onChange={date => handleDateChange('replanFinishDate', date)}
                value={formik.values.replanFinishDate}
                error={
                  formik.touched.replanFinishDate &&
                  Boolean(formik.errors.replanFinishDate)
                }
                helperText={
                  (formik.touched.replanFinishDate &&
                    formik.errors.replanFinishDate) ||
                  DEFAULT_HELPER_TEXT
                }
              />
            </Grid>
          </Grid>
          {/* END ROW 1 */}

          {/* START ROW 2 */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl
                onChange={formik.handleChange}
                error={formik.touched.outage && Boolean(formik.errors.outage)}
              >
                <FormLabel className={classes.formLabel} component="legend">
                  <span className={classes.formInput}>
                    A atividade possui indisponibilidade de geração?
                  </span>
                  {queryOutageOptions?.isLoading && (
                    <span>
                      <CircularProgress color="inherit" size={25} />
                    </span>
                  )}
                </FormLabel>
                <RadioGroup
                  value={formik.values.outage}
                  id="outage"
                  name="outage"
                >
                  {outageOptions.map(({ value, label }) => (
                    <FormControlLabel
                      value={value}
                      control={<Radio color="primary" />}
                      label={label}
                    />
                  ))}
                  <FormHelperText id="outage">
                    {(formik.touched.outage && formik.errors.outage) ||
                      DEFAULT_HELPER_TEXT}
                  </FormHelperText>
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
          {/* END ROW 2 */}

          {/* START ROW 3 */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl
                onChange={formik.handleChange}
                error={
                  formik.touched.utilization &&
                  Boolean(formik.errors.utilization)
                }
              >
                <FormLabel className={classes.formLabel} component="legend">
                  <span className={classes.formInput}>
                    A atividade vai ser realizada em aproveitamento?
                  </span>
                  {queryUtilizationOptions?.isLoading && (
                    <span>
                      <CircularProgress color="inherit" size={25} />
                    </span>
                  )}
                </FormLabel>
                <RadioGroup
                  value={formik.values.utilization}
                  id="utilization"
                  name="utilization"
                >
                  {utilizationOptions.map(({ value, label }) => (
                    <FormControlLabel
                      value={value}
                      control={<Radio color="primary" />}
                      label={label}
                    />
                  ))}
                  <FormHelperText id="utilization">
                    {(formik.touched.utilization &&
                      formik.errors.utilization) ||
                      DEFAULT_HELPER_TEXT}
                  </FormHelperText>
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
          {/* END ROW 3 */}
        </Grid>
      </form>
    );
  }, [
    classes.bodyContent,
    classes.formInput,
    classes.formLabel,
    classes.formMessage,
    formik.errors.outage,
    formik.errors.utilization,
    formik.errors.replanFinishDate,
    formik.errors.replanStartDate,
    formik.handleChange,
    formik.handleSubmit,
    formik.touched.outage,
    formik.touched.utilization,
    formik.touched.replanFinishDate,
    formik.touched.replanStartDate,
    formik.values.outage,
    formik.values.utilization,
    formik.values.replanFinishDate,
    formik.values.replanStartDate,
    handleDateChange,
    outageOptions,
    utilizationOptions,
    queryOutageOptions?.isLoading,
    queryUtilizationOptions?.isLoading,
  ]);

  const renderFooter = React.useCallback(() => {
    return (
      <div className={classes.footer}>
        <Button color="primary" onClick={onClose}>
          Cancelar
        </Button>
        <ButtonWithLoading
          form={FORM_ID}
          disabled={!formik.dirty || !formik.isValid}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
        >
          Salvar
        </ButtonWithLoading>
      </div>
    );
  }, [classes.footer, formik.dirty, formik.isValid, loadingSaveForm, onClose]);

  return (
    <>
      <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
        <ActionsButtonsBaseModal
          title="Replanejar"
          body={renderBody()}
          footer={renderFooter()}
          open={open}
          onClose={onClose}
          width="629px"
        >
          {children}
        </ActionsButtonsBaseModal>
      </MuiPickersUtilsProvider>
    </>
  );
};

export default ModalReplan;
